import React from "react";
import { CommissionHistory, CommissionStatus } from "types/affiliation.types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { formatDate } from "@shared/utils/date";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { checkSearch } from "@shared/utils/misc";
import { useEarnings } from "features/affiliation/common/hooks";
import { COMMISSION_STATUS_DISPLAY_NAME } from "features/affiliation/common/constants";
import { getReferralOrPromoCode } from "features/affiliation/common/utils";
import EarningsEmptyState from "features/affiliation/common/EarningsEmptyState";
import Status from "features/affiliation/common/Status";
import EarningsTable from "features/affiliation/Earnings/EarningsTable";
import TabLayout from "features/affiliation/NewLook/TabLayout";
import TabSummary from "features/affiliation/NewLook/TabSummary";
import TablePanel from "features/affiliation/NewLook/TablePanel";
import RowList, {
  Line,
  RowFigure,
  RowMeta,
  RowSubFigure,
  RowTitle,
} from "features/affiliation/NewLook/RowList";
import EarningsByMonthPanel from "features/affiliation/NewLook/EarningsByMonthPanel";
import useAffiliationTotals from "features/affiliation/NewLook/useAffiliationTotals";
import { formatUsd } from "features/affiliation/NewLook/newLookUtils";

/**
 * The earnings tab in the new look: total earnings lead (confirmed and
 * awaiting confirmation beside it), the month's daily earnings, then every
 * commission.
 */
const NewLookEarnings = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { commissionHistoryQuery } = useEarnings();
  const commissions = commissionHistoryQuery.data;
  const loading = commissionHistoryQuery.isLoading;
  const totals = useAffiliationTotals();

  const renderRow = (commission: CommissionHistory) => (
    <>
      <Line>
        <RowTitle>{commission.customer_id}</RowTitle>
        <RowFigure>{formatUsd(commission.commission)}</RowFigure>
      </Line>
      <Line>
        <RowMeta>{formatDate(commission.time, true, true)}</RowMeta>
        <Status
          itemStatus={CommissionStatus}
          status={commission.status}
          statusDisplayName={t(
            COMMISSION_STATUS_DISPLAY_NAME[commission.status]
          )}
        />
      </Line>
      <Line>
        <RowMeta>
          {getReferralOrPromoCode(commission.referral_code, commission.source)}
        </RowMeta>
        <RowSubFigure>
          {t("earnings.revenue")} {formatUsd(commission.revenue)}
        </RowSubFigure>
      </Line>
    </>
  );

  return (
    <TabLayout
      data-testid="affiliation-earnings"
      summary={
        <TabSummary
          loading={loading}
          lead={{
            label: t("overview.total_earnings"),
            value: totals.totalEarnings,
            currency: "$",
          }}
          items={[
            {
              key: "confirmed",
              label: t("earnings.status_display_name.confirmed"),
              value: formatUsd(totals.confirmed),
              tooltip: t("earnings.pie_chart_tooltips.amount_earned"),
            },
            {
              key: "awaiting",
              label: t("earnings.status_display_name.pending"),
              value: formatUsd(totals.awaitingConfirmation),
              tooltip: t("earnings.pie_chart_tooltips.amount_not_confirmed"),
            },
          ]}
        />
      }
      aside={<EarningsByMonthPanel />}
      asideOnTop={true}
      table={
        <TablePanel
          title={t("earnings.title")}
          count={commissions?.length}
          loading={loading}
          table={<EarningsTable commissions={commissions} isLoading={false} />}
        />
      }
      list={
        <RowList<CommissionHistory>
          title={t("earnings.title")}
          items={commissions}
          getKey={(commission) =>
            `${commission.customer_id}-${commission.time}-${commission.referral_code}`
          }
          renderRow={renderRow}
          filter={(commission, search) =>
            checkSearch(commission.referral_code, search)
          }
          loading={loading}
          sortOptions={[
            {
              key: "time",
              label: t("earnings.created_date"),
              compare: (a, b) => sortByNumberProperty(a, b, "time"),
            },
            {
              key: "commission",
              label: t("earnings.commission"),
              compare: (a, b) => sortByNumberProperty(a, b, "commission"),
            },
            {
              key: "revenue",
              label: t("earnings.revenue"),
              compare: (a, b) => sortByNumberProperty(a, b, "revenue"),
            },
            {
              key: "status",
              label: t("earnings.status"),
              compare: (a, b) => sortByNumberProperty(a, b, "status"),
            },
            {
              key: "customer_id",
              label: t("earnings.customer_id"),
              compare: (a, b) => sortByNumberProperty(a, b, "customer_id"),
            },
            {
              key: "referral_code",
              label: t("earnings.referral_link_promo"),
              compare: (a, b) => sortByStringProperty(a, b, "referral_code"),
            },
          ]}
          empty={<EarningsEmptyState />}
          searchEmpty={<EarningsEmptyState isSearch={true} />}
        />
      }
    />
  );
};

export default NewLookEarnings;
